/**
 * CustomDiffuserSystemPage.tsx — Multi-loop air-distribution system builder
 *
 * Extends the single-diffuser Custom Diffuser page into a full system. The
 * user can configure up to 6 independent AIR LOOPS, each fed by its own
 * dedicated air pump (blower). Each loop carries its own array of diffusers
 * with adjustable geometry, pore pattern, hole count per row/column, and
 * connection topology.
 *
 * What's new vs CustomDiffuserPage:
 *   - Multi-loop architecture (separate blowers / manifolds)
 *   - Per-loop pore pattern (rows × cols × density)
 *   - Per-loop module count, diffuser type, airflow
 *   - Live SVG network diagram (blower → manifold → diffusers)
 *   - Per-loop hydraulics + aggregate system stats
 *
 * Physics:
 *   - Bubble size (Fritz + Loubière): d ∝ (σ d_pore / ρg)^(1/3) · q_pore corr
 *   - SOTE per metre (Stenstrom & Rosso 2008)
 *   - Blower isentropic compression power
 *   - Manifold velocity (continuity) at branch
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Sparkles, ArrowLeft, Plus, Trash2, Save, Info,
  Wind, Layers, Power,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { toast } from 'sonner';

const KEY = 'rb_custom_diff_system_v1';
const G = 9.81;
const P_ATM = 101_325;
const RHO_AIR = 1.293;
const O2_MASS_FRAC = 0.2314;
const GAMMA_AIR = 1.4;

// ─── Physics helpers ─────────────────────────────────────────────────────────
function rhoW(T: number) { return 999.842 + 6.79e-2*T - 9.09e-3*T*T + 1.0e-4*T*T*T; }
function sigmaW(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4*T); }

function bubbleDiamMm(poreUm: number, qPerDiff_Nm3h: number, pores: number, T: number): number {
  const d_p = poreUm / 1e6;
  const sigma = sigmaW(T);
  const rho = rhoW(T);
  const d_static = Math.pow((6 * sigma * d_p) / (rho * G), 1/3);
  const q_pore = (qPerDiff_Nm3h / 3600) / Math.max(1, pores);
  const corr = 1 + 0.9 * Math.pow(Math.max(q_pore * 1e9, 0.001), 0.25);
  return d_static * corr * 1000;
}

function sotePerMetre(d_b_mm: number): number {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_b_mm)));
}

function blowerKW(q_Nm3h: number, depth_m: number, dropKpa: number, eff: number, safetyPct: number): number {
  const Q = q_Nm3h / 3600;
  const P_out = P_ATM + (rhoW(15) * G * depth_m + dropKpa * 1000) * (1 + safetyPct/100);
  const exp = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / Math.max(0.1, eff) / 1000;
}

// ─── Types ───────────────────────────────────────────────────────────────────
interface AirLoop {
  id: string;
  name: string;
  enabled: boolean;
  // Loop topology
  moduleCount: number;          // number of modules (diffuser frames) in this loop
  diffusersPerModule: number;   // diffusers per module frame
  // Pore pattern
  poreRows: number;             // rows of pores
  poreCols: number;             // cols of pores
  poreDensityPerCm2: number;    // density per cm² (for verification)
  poreSizeUm: number;
  // Diffuser geometry
  diffShape: 'disc' | 'tube' | 'strip';
  diffDiamMm: number;           // disc diameter or tube outer diameter
  diffLengthMm: number;         // tube/strip length
  // Air supply
  airflowPerDiffNm3h: number;
  // Dedicated blower
  blowerEfficiency: number;     // 0-1
  blowerType: 'roots' | 'screw' | 'turbo';
  // Manifold
  manifoldDiamMm: number;       // primary manifold pipe Ø
  manifoldRoughness_um: number;
}

interface SystemConfig {
  reservoirAreaM2: number;
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
  diffuserDropKpa: number;
  safetyPct: number;
  loops: AirLoop[];
}

function uid() { return 'l' + Math.random().toString(36).slice(2, 8); }

function defaultLoop(idx: number, defaults?: Partial<AirLoop>): AirLoop {
  return {
    id: uid(),
    name: `Loop ${idx}`,
    enabled: true,
    moduleCount: 6,
    diffusersPerModule: 8,
    poreRows: 14,
    poreCols: 14,
    poreDensityPerCm2: 35,
    poreSizeUm: 80,
    diffShape: 'disc',
    diffDiamMm: 250,
    diffLengthMm: 1500,
    airflowPerDiffNm3h: 1.2,
    blowerEfficiency: 0.65,
    blowerType: 'screw',
    manifoldDiamMm: 75,
    manifoldRoughness_um: 1.5,
    ...defaults,
  };
}

const DEFAULT_CONFIG: SystemConfig = {
  reservoirAreaM2: 34_000,
  waterDepthM: 3.0,
  mountingHeightM: 0.40,
  waterTempC: 15,
  diffuserDropKpa: 1.5,
  safetyPct: 10,
  loops: [
    defaultLoop(1, { name: 'Loop A — Deep zone',   blowerType: 'screw', moduleCount: 8 }),
    defaultLoop(2, { name: 'Loop B — Mid zone',    blowerType: 'screw', moduleCount: 6 }),
    defaultLoop(3, { name: 'Loop C — Shallow zone', blowerType: 'roots', moduleCount: 4, diffuserShape: 'tube' as never }),
  ],
};

const BLOWER_EFF: Record<AirLoop['blowerType'], number> = { roots: 0.58, screw: 0.72, turbo: 0.80 };
const BLOWER_COLOUR: Record<AirLoop['blowerType'], string> = { roots: '#a855f7', screw: '#06b6d4', turbo: '#22c55e' };

// ─── Per-loop computation ────────────────────────────────────────────────────
function computeLoop(loop: AirLoop, sys: SystemConfig) {
  const T = sys.waterTempC;
  const H_eff = Math.max(0.3, sys.waterDepthM - sys.mountingHeightM);

  // Pore count per diffuser: rows × cols × diff-area fraction (clamped to physical max)
  const poresPerDiff_grid = loop.poreRows * loop.poreCols;
  // Cross-check via density: pores = density × area_cm²
  let diffArea_cm2: number;
  if (loop.diffShape === 'disc' || loop.diffShape === 'dome') {
    diffArea_cm2 = Math.PI * (loop.diffDiamMm / 20) ** 2;   // mm → cm radius
  } else if (loop.diffShape === 'tube') {
    diffArea_cm2 = Math.PI * (loop.diffDiamMm / 10) * (loop.diffLengthMm / 10);
  } else {
    diffArea_cm2 = (loop.diffLengthMm / 10) * (loop.diffDiamMm / 10);   // strip
  }
  const poresPerDiff_density = Math.round(loop.poreDensityPerCm2 * diffArea_cm2 * 0.7);  // 70% active area
  // Use the lower of grid or density — physically constrained
  const poresPerDiff = Math.min(poresPerDiff_grid, poresPerDiff_density);

  const totalDiffusers = loop.moduleCount * loop.diffusersPerModule;
  const totalPores = poresPerDiff * totalDiffusers;

  // Loop airflow demand
  const totalAirflow_Nm3h = loop.airflowPerDiffNm3h * totalDiffusers;

  // Bubble & SOTE
  const d_b_mm = bubbleDiamMm(loop.poreSizeUm, loop.airflowPerDiffNm3h, poresPerDiff, T);
  const sote_per_m = sotePerMetre(d_b_mm);
  const sote_total_pct = Math.min(99, sote_per_m * H_eff);

  // Loop SOTR / AOTR
  const SOTR_loop_kgH = totalAirflow_Nm3h * RHO_AIR * O2_MASS_FRAC * sote_total_pct / 100;
  const AOTR_loop_kgH = SOTR_loop_kgH * 0.85 * 0.98 * Math.pow(1.024, T - 20)
                     * Math.max(0, 9.0 - 2.0) / 9.09;

  // Loop blower power
  const effOverride = loop.blowerEfficiency || BLOWER_EFF[loop.blowerType];
  const blower_kW = blowerKW(totalAirflow_Nm3h, sys.waterDepthM, sys.diffuserDropKpa, effOverride, sys.safetyPct);

  // Manifold velocity
  const A_manifold = Math.PI * (loop.manifoldDiamMm / 2000) ** 2;
  const manifoldVelocity_ms = (totalAirflow_Nm3h / 3600) / Math.max(1e-9, A_manifold);

  return {
    poresPerDiff, poresPerDiff_grid, poresPerDiff_density, totalDiffusers, totalPores,
    totalAirflow_Nm3h, d_b_mm, sote_per_m, sote_total_pct,
    SOTR_loop_kgH, AOTR_loop_kgH, blower_kW,
    A_manifold, manifoldVelocity_ms, H_eff, diffArea_cm2,
  };
}

function fmt(n: number, d = 2): string {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function CustomDiffuserSystemPage() {
  const navigate = useNavigate();
  const [sys, setSys] = useState<SystemConfig>(() => loadPersisted<SystemConfig>(KEY, DEFAULT_CONFIG));

  useEffect(() => { savePersisted(KEY, sys); }, [sys]);

  const updSys = (p: Partial<SystemConfig>) => setSys(s => ({ ...s, ...p }));
  const updLoop = (id: string, p: Partial<AirLoop>) =>
    setSys(s => ({ ...s, loops: s.loops.map(l => l.id === id ? { ...l, ...p } : l) }));
  const addLoop = () => setSys(s => ({ ...s, loops: [...s.loops, defaultLoop(s.loops.length + 1)] }));
  const removeLoop = (id: string) =>
    setSys(s => ({ ...s, loops: s.loops.filter(l => l.id !== id) }));

  // Per-loop results
  const loopResults = useMemo(() => sys.loops.map(l => ({ loop: l, R: computeLoop(l, sys) })), [sys]);

  // Aggregate stats
  const agg = useMemo(() => {
    const active = loopResults.filter(({ loop }) => loop.enabled);
    return {
      totalLoops: active.length,
      totalModules: active.reduce((s, { loop }) => s + loop.moduleCount, 0),
      totalDiffusers: active.reduce((s, { R }) => s + R.totalDiffusers, 0),
      totalPores: active.reduce((s, { R }) => s + R.totalPores, 0),
      totalAirflow_Nm3h: active.reduce((s, { R }) => s + R.totalAirflow_Nm3h, 0),
      totalBlower_kW: active.reduce((s, { R }) => s + R.blower_kW, 0),
      totalSOTR_kgH: active.reduce((s, { R }) => s + R.SOTR_loop_kgH, 0),
      totalAOTR_kgH: active.reduce((s, { R }) => s + R.AOTR_loop_kgH, 0),
    };
  }, [loopResults]);

  const saveAsSystemSpec = () => {
    savePersisted(KEY, sys);
    toast.success('System spec saved');
  };

  // ── SVG geometry ─────────────────────────────────────────────────────────
  const svgW = 800, svgH = 60 + loopResults.length * 95 + 20;
  const blowerX = 40, manifoldX = 130;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Sparkles}
        iconColor="text-violet-400"
        title="Custom Diffuser System Builder"
        subtitle="Multi-loop air-distribution system with independent blowers, pore-pattern control, and module assignment"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={saveAsSystemSpec}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => navigate('/custom-diffuser')}
              className="inline-flex items-center gap-2 rounded-lg border border-violet-700 bg-violet-900/30 px-3 py-1.5 text-sm text-violet-200 hover:bg-violet-900/50">
              Single-Diffuser Designer
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Aggregate tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Air loops</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{agg.totalLoops}</div>
          <div className="text-xs text-slate-500 mt-0.5">independent blowers</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Modules</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{agg.totalModules}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Diffusers</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{agg.totalDiffusers}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Pores total</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{agg.totalPores.toLocaleString()}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-3">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Total airflow</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(agg.totalAirflow_Nm3h, 0)} Nm³/h</div>
        </div>
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-3">
          <div className="text-xs text-emerald-300 uppercase tracking-wider">Total blower power</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{fmt(agg.totalBlower_kW, 1)} kW</div>
          <div className="text-xs text-slate-500 mt-0.5">across {agg.totalLoops} blowers</div>
        </div>
      </div>

      {/* System-level inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">System inputs (apply to all loops)</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          {[
            { k: 'reservoirAreaM2', label: 'Reservoir area (m²)', step: 1000 },
            { k: 'waterDepthM',     label: 'Water depth (m)',      step: 0.1 },
            { k: 'mountingHeightM', label: 'Mounting height (m)',  step: 0.05 },
            { k: 'waterTempC',      label: 'Water T (°C)',         step: 0.5 },
            { k: 'diffuserDropKpa', label: 'Diffuser ΔP (kPa)',    step: 0.1 },
            { k: 'safetyPct',       label: 'Safety margin (%)',    step: 1 },
          ].map(f => (
            <div key={f.k}>
              <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
              <input type="number" step={f.step}
                value={(sys as unknown as Record<string, number>)[f.k]}
                onChange={e => updSys({ [f.k]: Number(e.target.value) } as Partial<SystemConfig>)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
            </div>
          ))}
        </div>
      </div>

      {/* SVG visualisation — air-distribution network */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-100">Air-distribution network</div>
          <button onClick={addLoop}
            className="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-violet-500">
            <Plus className="w-3.5 h-3.5" /> Add loop
          </button>
        </div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {loopResults.map(({ loop, R }, li) => {
            const yBase = 30 + li * 95;
            const blowerColour = loop.enabled ? BLOWER_COLOUR[loop.blowerType] : '#475569';
            const lineColour = loop.enabled ? '#cbd5e1' : '#374151';
            // Diffuser positions along the loop
            const diffsToShow = Math.min(loop.moduleCount * loop.diffusersPerModule, 24);
            const moduleStartX = manifoldX + 40;
            const moduleEndX = svgW - 30;
            const xStep = (moduleEndX - moduleStartX) / Math.max(1, loop.moduleCount);
            return (
              <g key={loop.id} opacity={loop.enabled ? 1 : 0.4}>
                {/* Blower box */}
                <rect x={blowerX - 25} y={yBase - 20} width={50} height={40}
                  fill={blowerColour} stroke="#0a1628" strokeWidth={1.5} rx={4} />
                <text x={blowerX} y={yBase - 4} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="bold">
                  {loop.blowerType.toUpperCase()}
                </text>
                <text x={blowerX} y={yBase + 8} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="bold">
                  {fmt(R.blower_kW, 1)} kW
                </text>
                {/* Manifold pipe */}
                <line x1={blowerX + 25} y1={yBase} x2={moduleStartX - 5} y2={yBase}
                  stroke={lineColour} strokeWidth={Math.max(3, loop.manifoldDiamMm / 15)} strokeLinecap="round" />
                <text x={(blowerX + moduleStartX) / 2} y={yBase - 8} textAnchor="middle" fontSize={8} fill="#94a3b8">
                  Ø{loop.manifoldDiamMm} mm · {fmt(R.manifoldVelocity_ms, 1)} m/s
                </text>
                {/* Loop name & meta */}
                <text x={blowerX - 22} y={yBase - 25} fontSize={9} fill="#cbd5e1" fontWeight="bold">{loop.name}</text>
                <text x={moduleStartX} y={yBase + 38} fontSize={8} fill="#94a3b8">
                  {loop.moduleCount} modules × {loop.diffusersPerModule} diff = {R.totalDiffusers}
                </text>
                <text x={moduleStartX} y={yBase + 50} fontSize={8} fill="#94a3b8">
                  d_b = {fmt(R.d_b_mm, 2)} mm · SOTE {fmt(R.sote_per_m, 1)} %/m · Q {fmt(R.totalAirflow_Nm3h, 0)} Nm³/h
                </text>
                {/* Module markers along the manifold */}
                {Array.from({ length: Math.min(loop.moduleCount, 12) }, (_, i) => {
                  const x = moduleStartX + i * xStep + xStep / 2;
                  return (
                    <g key={i}>
                      {/* Tee branch */}
                      <line x1={x} y1={yBase - 2} x2={x} y2={yBase + 14} stroke={lineColour} strokeWidth={1.5} />
                      {/* Module square (frame) */}
                      <rect x={x - 6} y={yBase + 14} width={12} height={10}
                        fill="#fbbf24" stroke="#0a1628" strokeWidth={0.6} />
                      {/* Pore-dot pattern inside module */}
                      {(() => {
                        const dots = [];
                        const rows = Math.min(3, Math.max(2, Math.round(loop.poreRows / 5)));
                        const cols = Math.min(3, Math.max(2, Math.round(loop.poreCols / 5)));
                        for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
                          dots.push(
                            <circle key={`${r}-${c}`}
                              cx={x - 4 + c * (8 / Math.max(1, cols - 1))}
                              cy={yBase + 16 + r * (6 / Math.max(1, rows - 1))}
                              r={0.4} fill="#0ea5e9" />
                          );
                        }
                        return dots;
                      })()}
                      {/* Module number */}
                      <text x={x} y={yBase + 35} textAnchor="middle" fontSize={6} fill="#fcd34d">M{i + 1}</text>
                    </g>
                  );
                })}
                {loop.moduleCount > 12 && (
                  <text x={moduleEndX - 10} y={yBase + 4} fontSize={8} fill="#94a3b8">+{loop.moduleCount - 12} more</text>
                )}
                {/* Continuation indicator if more diffusers per module */}
                <text x={moduleEndX} y={yBase + 28} textAnchor="end" fontSize={7} fill="#94a3b8">
                  {loop.diffusersPerModule}× diff per frame · pores {loop.poreRows}×{loop.poreCols}
                </text>
                {/* Flow arrows */}
                {[blowerX + 50, blowerX + 90].map((x, i) => (
                  <polygon key={i} points={`${x},${yBase - 2} ${x + 4},${yBase + 1} ${x},${yBase + 4}`}
                    fill={blowerColour} opacity={0.9} />
                ))}
                {/* Connection point at bottom */}
                <line x1={blowerX} y1={yBase + 20} x2={blowerX} y2={yBase + 38} stroke={lineColour} strokeWidth={1} strokeDasharray="2 2" />
                <text x={blowerX} y={yBase + 48} textAnchor="middle" fontSize={7} fill="#94a3b8">
                  η {((loop.blowerEfficiency || BLOWER_EFF[loop.blowerType]) * 100).toFixed(0)}%
                </text>
                {!loop.enabled && (
                  <text x={(blowerX + moduleEndX) / 2} y={yBase} fontSize={14} fill="#ef4444" textAnchor="middle" fontWeight="bold">[OFF]</text>
                )}
              </g>
            );
          })}
        </svg>
        <div className="text-[10px] text-slate-500 mt-2 flex flex-wrap gap-3">
          {(Object.keys(BLOWER_COLOUR) as AirLoop['blowerType'][]).map(t => (
            <span key={t}><span className="inline-block w-3 h-3 rounded align-middle mr-1" style={{ background: BLOWER_COLOUR[t] }} />{t} blower</span>
          ))}
          <span><span className="inline-block w-3 h-3 bg-amber-300 align-middle mr-1" /> module frame</span>
          <span><span className="inline-block w-1 h-1 bg-sky-500 align-middle mr-1" /> pore (illustrative)</span>
        </div>
      </div>

      {/* Per-loop configuration */}
      {loopResults.map(({ loop, R }) => (
        <div key={loop.id} className={`rounded-xl border-2 p-4 ${loop.enabled ? 'border-violet-700/40 bg-violet-900/5' : 'border-slate-700 bg-slate-900 opacity-70'}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <input type="text" value={loop.name}
                onChange={e => updLoop(loop.id, { name: e.target.value })}
                className="text-sm font-bold text-violet-200 bg-transparent border-b border-violet-700/40 focus:outline-none focus:border-violet-500 flex-1 min-w-0 max-w-xs" />
              <label className="flex items-center gap-1.5 text-xs text-slate-400">
                <input type="checkbox" checked={loop.enabled}
                  onChange={e => updLoop(loop.id, { enabled: e.target.checked })}
                  className="accent-violet-500" />
                {loop.enabled ? <><Power className="w-3 h-3 text-emerald-400" /> active</> : <><Power className="w-3 h-3 text-slate-500" /> off</>}
              </label>
            </div>
            <button onClick={() => removeLoop(loop.id)}
              className="text-slate-500 hover:text-red-400 text-xs inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>

          {/* Per-loop tiles */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1.5">
              <div className="text-[10px] text-slate-500 uppercase">Diffusers</div>
              <div className="text-sm font-bold text-slate-100">{R.totalDiffusers}</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1.5">
              <div className="text-[10px] text-slate-500 uppercase">Pores / diff</div>
              <div className="text-sm font-bold text-slate-100">{R.poresPerDiff.toLocaleString()}</div>
              <div className="text-[9px] text-slate-500">grid {R.poresPerDiff_grid} · density {R.poresPerDiff_density}</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1.5">
              <div className="text-[10px] text-slate-500 uppercase">Bubble Ø</div>
              <div className="text-sm font-bold text-emerald-300">{fmt(R.d_b_mm, 2)} mm</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1.5">
              <div className="text-[10px] text-slate-500 uppercase">SOTE/m</div>
              <div className="text-sm font-bold text-slate-100">{fmt(R.sote_per_m, 2)} %</div>
            </div>
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1.5">
              <div className="text-[10px] text-slate-500 uppercase">Loop AOTR</div>
              <div className="text-sm font-bold text-slate-100">{fmt(R.AOTR_loop_kgH, 2)} kg/h</div>
            </div>
            <div className="rounded-md border border-emerald-700/40 bg-emerald-900/15 px-2 py-1.5">
              <div className="text-[10px] text-emerald-300 uppercase">Blower kW</div>
              <div className="text-sm font-bold text-emerald-200">{fmt(R.blower_kW, 2)} kW</div>
              <div className="text-[9px] text-slate-500">{loop.blowerType}, η {((loop.blowerEfficiency || BLOWER_EFF[loop.blowerType]) * 100).toFixed(0)}%</div>
            </div>
          </div>

          {/* Per-loop inputs grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
            {[
              { k: 'moduleCount',         label: 'Modules',         step: 1, min: 1 },
              { k: 'diffusersPerModule',  label: 'Diffusers / module', step: 1, min: 1 },
              { k: 'airflowPerDiffNm3h',  label: 'Air / diff (Nm³/h)', step: 0.1 },
              { k: 'poreRows',            label: 'Pore rows', step: 1, min: 1 },
              { k: 'poreCols',            label: 'Pore cols', step: 1, min: 1 },
              { k: 'poreSizeUm',          label: 'Pore Ø (µm)', step: 5, min: 10 },
              { k: 'poreDensityPerCm2',   label: 'Density (pores/cm²)', step: 5 },
              { k: 'diffDiamMm',          label: 'Diff Ø (mm)', step: 10 },
              { k: 'diffLengthMm',        label: 'Length (mm)', step: 50 },
              { k: 'manifoldDiamMm',      label: 'Manifold Ø (mm)', step: 5 },
              { k: 'blowerEfficiency',    label: 'Blower η (0-1)', step: 0.01 },
            ].map(f => (
              <div key={f.k}>
                <label className="text-slate-400 block mb-0.5">{f.label}</label>
                <input type="number" step={f.step} min={f.min ?? 0}
                  value={(loop as unknown as Record<string, number>)[f.k]}
                  onChange={e => updLoop(loop.id, { [f.k]: Number(e.target.value) } as Partial<AirLoop>)}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100" />
              </div>
            ))}
            <div>
              <label className="text-slate-400 block mb-0.5">Diff shape</label>
              <select value={loop.diffShape}
                onChange={e => updLoop(loop.id, { diffShape: e.target.value as AirLoop['diffShape'] })}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100">
                <option value="disc">Disc</option>
                <option value="tube">Tube</option>
                <option value="strip">Strip</option>
              </select>
            </div>
            <div>
              <label className="text-slate-400 block mb-0.5">Blower type</label>
              <select value={loop.blowerType}
                onChange={e => updLoop(loop.id, { blowerType: e.target.value as AirLoop['blowerType'] })}
                className="w-full bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-slate-100">
                <option value="roots">Roots (η 58%)</option>
                <option value="screw">Screw (η 72%)</option>
                <option value="turbo">Turbo (η 80%)</option>
              </select>
            </div>
          </div>
        </div>
      ))}

      {/* Aggregate summary table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Per-loop summary</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Loop</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Blower</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Modules</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Diff</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Pores</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Q (Nm³/h)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">v_man (m/s)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">d_b (mm)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">SOTE/m %</th>
                <th className="px-2 py-1.5 text-right text-slate-300">AOTR kg/h</th>
                <th className="px-2 py-1.5 text-right text-slate-300">kW</th>
              </tr>
            </thead>
            <tbody>
              {loopResults.map(({ loop, R }) => (
                <tr key={loop.id} className={`border-b border-slate-800 ${!loop.enabled ? 'opacity-50' : ''}`}>
                  <td className="px-2 py-1 text-slate-100">{loop.name}</td>
                  <td className="px-2 py-1 text-slate-300">{loop.blowerType}</td>
                  <td className="px-2 py-1 text-right text-slate-100">{loop.moduleCount}</td>
                  <td className="px-2 py-1 text-right text-slate-100">{R.totalDiffusers}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{R.totalPores.toLocaleString()}</td>
                  <td className="px-2 py-1 text-right text-slate-100">{fmt(R.totalAirflow_Nm3h, 0)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(R.manifoldVelocity_ms, 1)}</td>
                  <td className="px-2 py-1 text-right text-emerald-300 font-semibold">{fmt(R.d_b_mm, 2)}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{fmt(R.sote_per_m, 2)}</td>
                  <td className="px-2 py-1 text-right text-slate-100">{fmt(R.AOTR_loop_kgH, 2)}</td>
                  <td className="px-2 py-1 text-right text-emerald-300">{fmt(R.blower_kW, 1)}</td>
                </tr>
              ))}
              <tr className="font-semibold bg-emerald-900/15">
                <td className="px-2 py-1.5 text-emerald-200">Total ({agg.totalLoops} active)</td>
                <td colSpan={1}></td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{agg.totalModules}</td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{agg.totalDiffusers}</td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{agg.totalPores.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{fmt(agg.totalAirflow_Nm3h, 0)}</td>
                <td colSpan={3}></td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{fmt(agg.totalAOTR_kgH, 2)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-200">{fmt(agg.totalBlower_kW, 1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Multi-loop architecture rationale
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Splitting an aeration scheme into <strong>multiple independent air loops</strong>, each with its
            own dedicated blower, gives operational flexibility a single-blower scheme cannot match:
          </p>
          <ul className="list-disc ml-5 space-y-0.5">
            <li><strong className="text-emerald-300">Zone-specific tuning</strong> — deep / mid / shallow zones can run at different airflows without throttling losses.</li>
            <li><strong className="text-emerald-300">Redundancy</strong> — one blower out of service still leaves the rest operational. Critical when DWI continuity is required.</li>
            <li><strong className="text-emerald-300">Maintenance staggering</strong> — service each loop independently without shutting down the reservoir.</li>
            <li><strong className="text-emerald-300">Right-sized blowers</strong> — small zones can use efficient screw blowers; large zones can use turbo. Mixing types is good practice.</li>
            <li><strong className="text-emerald-300">Diurnal load following</strong> — switch loops on/off as DO demand swings between night and day.</li>
          </ul>
          <p>
            The <strong>pore pattern</strong> (rows × cols) is constrained by the diffuser-area density check.
            The page reports both grid pores and density pores, then uses the lower value — physically
            impossible to fit more pores than the membrane area allows.
          </p>
          <p>
            <strong className="text-amber-300">Bubble size and SOTE</strong> are computed per loop using
            Fritz 1935 + Loubière 2003 with the actual pores-per-diffuser. Stenstrom & Rosso 2008 gives
            SOTE per metre from bubble Ø. Loop blower power uses isentropic compression with the chosen
            blower-type efficiency.
          </p>
        </div>
      </div>
    </div>
  );
}
